import {
  createMeidenConnectApiAccessTokenProvider,
  useMeidenConnectApiTokenWarmup,
  type ApiAccessTokenProvider,
} from "@osakabe/auth-service-provider";
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { SessionBackedCurrentActorProvider, useCurrentActor } from "./use-current-actor";
import { useAuthSession } from "./auth-session-context";
import {
  getEmbedAccessToken,
  requestEmbedTokenRefresh,
  waitForEmbedToken,
  waitForNextEmbedToken,
} from "./embed-auth";
import type { WebConfig } from "../env";
import { getWebConfig } from "../env";

export type ApiRequest = (path: string, init?: RequestInit) => Promise<Response>;

/**
 * アクセストークンを取得（保存済み・リフレッシュとも）できなかったことを表すエラー。
 * 呼び出し側（アップロードのトースト等）が「サーバ障害」ではなく「サインイン情報の期限切れ」
 * として利用者に案内できるよう、汎用 Error と区別する。判定はバンドル境界の影響を受けない
 * name プロパティで行えるようにする。
 */
export class AccessTokenUnavailableError extends Error {
  constructor() {
    super("MEIDEN CONNECT のアクセストークンを取得できませんでした。");
    this.name = "AccessTokenUnavailableError";
  }
}

interface ApiRequestProviderProps {
  readonly children: ReactNode;
  readonly request: ApiRequest;
}

interface RuntimeApiRequestProviderProps {
  readonly children: ReactNode;
}

interface CreateApiRequestOptions {
  readonly fetchImpl?: typeof fetch | undefined;
  readonly getAccessToken?: ApiAccessTokenProvider | undefined;
  readonly getActorProfile?: (() => ActorProfileHeaders | undefined) | undefined;
}

const ApiRequestContext = createContext<ApiRequest | null>(null);
const ACTOR_DISPLAY_NAME_HEADER = "x-refinement-canvas-actor-name";
const ACTOR_AVATAR_IMAGE_URL_HEADER = "x-refinement-canvas-actor-avatar";

interface ActorProfileHeaders {
  readonly displayName: string;
  readonly avatarImageUrl?: string | undefined;
}

export function ApiRequestProvider({ children, request }: ApiRequestProviderProps): JSX.Element {
  return <ApiRequestContext.Provider value={request}>{children}</ApiRequestContext.Provider>;
}

export function useApiRequest(): ApiRequest {
  const request = useContext(ApiRequestContext);
  if (!request) {
    throw new Error("useApiRequest() は ApiRequestProvider 配下で呼び出してください。");
  }
  return request;
}

export function MockApiRequestProvider({ children }: RuntimeApiRequestProviderProps): JSX.Element {
  const config = getWebConfig();
  const request = useMemo(() => createApiRequest(config), [config]);
  return <ApiRequestProvider request={request}>{children}</ApiRequestProvider>;
}

export function AuthenticatedApiRequestProvider({
  children,
}: RuntimeApiRequestProviderProps): JSX.Element {
  // CurrentActor は SessionSnapshot から導出する。SDK Provider の状態には依存しない。
  return (
    <SessionBackedCurrentActorProvider>
      <AuthenticatedApiRequestProviderInner>{children}</AuthenticatedApiRequestProviderInner>
    </SessionBackedCurrentActorProvider>
  );
}

function AuthenticatedApiRequestProviderInner({
  children,
}: RuntimeApiRequestProviderProps): JSX.Element {
  const request = useAuthenticatedApiRequest();
  return <ApiRequestProvider request={request}>{children}</ApiRequestProvider>;
}

/** 認証セッションと現在のアクターから、参照が安定した API リクエスト関数を組み立てる。 */
export function useAuthenticatedApiRequest(): ApiRequest {
  const config = getWebConfig();
  const { user } = useAuthSession();
  const actor = useCurrentActor();
  const serviceOrigin = config.serviceOrigin ?? window.location.origin;
  // アクセストークン取得は package が提供する保存済みトークン優先経路を使う。
  // SDK Provider が main tree に無くても、IndexedDB / refresh 経由で安定して取得できる。
  const getAccessToken = useMemo<ApiAccessTokenProvider>(() => {
    if (config.authServiceOrigin === undefined || user === undefined) {
      return async () => undefined;
    }

    return createMeidenConnectApiAccessTokenProvider({
      authServiceOrigin: config.authServiceOrigin,
      serviceOrigin,
      userId: user.userId,
    });
  }, [config.authServiceOrigin, serviceOrigin, user?.userId]);
  useMeidenConnectApiTokenWarmup(getAccessToken);
  const actorProfileRef = useRef<ActorProfileHeaders>({
    displayName: actor.displayName,
    avatarImageUrl: actor.avatarImageUrl,
  });
  // 利用者の切り替えと同じ commit で子の layout effect が API を呼ぶ場合にも、
  // 切り替え後のプロフィールを参照できるよう passive effect より前に同期する。
  useLayoutEffect(() => {
    actorProfileRef.current = {
      displayName: actor.displayName,
      avatarImageUrl: actor.avatarImageUrl,
    };
  }, [actor.avatarImageUrl, actor.displayName]);
  const getActorProfile = useCallback(() => actorProfileRef.current, []);
  const request = useMemo(
    () => createApiRequest(config, { getAccessToken, getActorProfile }),
    [config, getAccessToken, getActorProfile]
  );
  return request;
}

/**
 * 参照専用の埋め込みモード用の ApiRequestProvider。
 *
 * 親フレーム（Connect Note）から postMessage で受け取ったトークン（embed-auth.ts が保持）を
 * Bearer に使う。IndexedDB / SDK Provider には一切依存しない（分離ストレージ安全）。actorProfile は
 * 付けない（サーバは Bearer の subject からアクターを導出し、閲覧のみで書き込みも行わない）。
 */
export function EmbedApiRequestProvider({ children }: RuntimeApiRequestProviderProps): JSX.Element {
  const config = getWebConfig();
  const request = useMemo(() => {
    const getAccessToken: ApiAccessTokenProvider = async (tokenOptions) => {
      if (tokenOptions?.forceRefresh === true) {
        // 401 retry 等: サーバに拒否された現行トークンを再利用しないよう、親へ再発行を要求し、
        // 「現行と異なる」新トークンの到着を待つ。timeout 時は undefined（retry を打ち切らせる）。
        const stale = getEmbedAccessToken();
        requestEmbedTokenRefresh();
        return await waitForNextEmbedToken(stale, 8_000);
      }
      return getEmbedAccessToken() ?? (await waitForEmbedToken(8_000));
    };
    return createApiRequest(config, { getAccessToken });
  }, [config]);
  return <ApiRequestProvider request={request}>{children}</ApiRequestProvider>;
}

export function createApiRequest(
  config: Pick<WebConfig, "apiBaseUrl" | "authMode">,
  options: CreateApiRequestOptions = {}
): ApiRequest {
  const fetchImpl = options.fetchImpl ?? fetch;
  const baseUrl = config.apiBaseUrl.replace(/\/$/, "");

  return async (path, init) => {
    const headers = buildApiHeaders(init?.headers);
    if (config.authMode === "required") {
      if (!options.getAccessToken) {
        throw new Error("authMode=required では getAccessToken が必要です。");
      }
      const accessToken =
        (await options.getAccessToken({ forceRefresh: false })) ??
        (await options.getAccessToken({ forceRefresh: true }));
      if (accessToken === undefined) {
        throw new AccessTokenUnavailableError();
      }
      headers.set("authorization", `Bearer ${accessToken}`);
      applyActorProfileHeaders(headers, options.getActorProfile?.());
    }

    // API 呼び出しは必ず同じ認証情報を伴うよう、credentials を include に固定する。
    const response = await fetchImpl(`${baseUrl}${path}`, {
      ...init,
      credentials: "include",
      headers,
    });
    // 401 は「認証で拒否され、リクエスト本体は処理されていない」ことを意味するため、
    // トークンを強制リフレッシュして 1 回だけ再送する。かつては GET / HEAD に限定していたが、
    // POST 等の書き込み系でも 401 なら未処理であることに変わりはなく再送は安全で、限定した
    // 結果「読み取りはリフレッシュで自己回復するのに、画像アップロード（presign POST）だけは
    // トークン失効で失敗し続ける」というメンバー依存の障害を生んでいた（2026-07-06 本番障害）。
    if (
      response.status === 401 &&
      config.authMode === "required" &&
      options.getAccessToken !== undefined
    ) {
      const refreshedAccessToken = await options.getAccessToken({ forceRefresh: true });
      if (refreshedAccessToken !== undefined) {
        const retryHeaders = new Headers(headers);
        retryHeaders.set("authorization", `Bearer ${refreshedAccessToken}`);
        return fetchImpl(`${baseUrl}${path}`, {
          ...init,
          credentials: "include",
          headers: retryHeaders,
        });
      }
      // 401 を受けたうえで強制リフレッシュにも失敗した＝サインイン情報の失効が確定したケース。
      // 401 Response をそのまま返すと呼び出し側で汎用の HTTP エラーに潰れ、利用者が再ログインで
      // 復旧できる状況なのに「サーバ側でエラー」と誤案内される（アップロード失敗トースト等）。
      // 初回取得失敗と同じ専用エラーへ揃え、失効案内（再読み込みの促し）に到達させる。
      throw new AccessTokenUnavailableError();
    }

    return response;
  };
}

function applyActorProfileHeaders(
  headers: Headers,
  profile: ActorProfileHeaders | undefined
): void {
  const displayName = profile?.displayName.trim();
  if (displayName) {
    // Fetch Headers は非 ASCII 文字を直接扱えないため、表示名だけ URI エンコードする。
    headers.set(ACTOR_DISPLAY_NAME_HEADER, encodeURIComponent(displayName));
  }
  const avatarImageUrl = profile?.avatarImageUrl?.trim();
  if (avatarImageUrl) {
    headers.set(ACTOR_AVATAR_IMAGE_URL_HEADER, avatarImageUrl);
  }
}

function buildApiHeaders(extra?: HeadersInit): Headers {
  const headers = new Headers(extra);
  if (!headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  return headers;
}
